/**
 * GenerateCode - world command for runtime code generation and execution.
 */

import { projectInfo } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';

/** Helper to check if a class is generic at runtime by examining its prototype chain and constructor */
function isGenericClass(cls: any): boolean {
  // Check if the class has a prototype with a constructor that has type parameters
  const prototype = cls.prototype;
  if (!prototype) return false;

  // Get the constructor function
  const constructor = prototype.constructor;
  if (!constructor) return false;

  // Check if the constructor has a type parameter by looking at its string representation
  const constructorStr = constructor.toString();
  return constructorStr.includes('<') && constructorStr.includes('>');
}

export async function generateClassTemplate(name: string, baseClassName: string, type: string) {
  const baseClassNameParts = baseClassName.split('.');
  const isEngineBaseClass = baseClassNameParts[0].startsWith('ENGINE');
  const baseName = isEngineBaseClass ? baseClassName : baseClassNameParts[baseClassNameParts.length - 1];
  const baseClassImport = isEngineBaseClass ? '' : `import { ${baseName}, ${baseName}Options } from './${baseName}.js';`;

  let isGenericBaseClass = false;
  if (isEngineBaseClass) {
    if (baseClassName === 'ENGINE.Component') {
      isGenericBaseClass = true;
    } else {
      try {
        const baseClass = ClassRegistry.getClass(baseClassName);
        isGenericBaseClass = isGenericClass(baseClass);
      } catch (error) {
        console.warn(`Error getting base class ${baseName}:`, error);
        isGenericBaseClass = false;
      }
    }
  } else {
    // Assume all games classes as non-generic
    // TODO: don't know how to check if game class is generic!
    isGenericBaseClass = false;
  }

  const classTemplate = `
import * as ENGINE from '@gnsx/genesys.js';
${baseClassImport}

export interface ${name}Options extends ${baseName}Options {
  // Define any specific options here
}

@ENGINE.GameClass()
export class ${name} extends ${baseName} {


  public override initialize(options?: ${name}Options): void {
    super.initialize(options);
  }

  ${type === 'actor' ? 'protected' : 'public'} override ${type === 'actor' ? 'doBeginPlay' : 'beginPlay'}(): void {
    super.${type === 'actor' ? 'doBeginPlay' : 'beginPlay'}();
  }
}
`;
  return classTemplate;
}

async function generateTemplateFile(
  actorName: string,
  path: string,
  baseClassName: string,
  type: string,
  templateGenerator: (name: string, baseClassName: string, type: string) => Promise<string>
) {
  const templateContent = await templateGenerator(actorName, baseClassName, type);
  const storageProvider = projectInfo.storageProvider;
  if (!storageProvider) {
    throw new Error(`No storage provider found in project context: ${projectInfo.project}`);
  }
  await storageProvider.uploadFile(AssetPath.fromString(path), templateContent);
}

export async function generateActorTemplateFile(actorName: string, path: string, baseClassName: string) {
  await generateTemplateFile(actorName, path, baseClassName, 'actor', generateClassTemplate);
}

export async function generateComponentTemplateFile(componentName: string, path: string, baseClassName: string) {
  await generateTemplateFile(componentName, path, baseClassName, 'component', generateClassTemplate);
}
